<template>
  <article>
    <div v-if="showTitle" class="container-fluid">
      <div class="row">
        <div class="col px-0">
          <SectionTitle has-line :title="'Documentos'" />
        </div>
      </div>
    </div>
    <div
      v-if="requiredDocumentTypes.length"
      class="container-fluid"
    >
      <div
        v-for="documentType in requiredDocumentTypes"
        :key="documentType.id"
        class="row box-container mb-3"
      >
        <div class="col-12 col-md-5 mb-4 mb-md-0">
          <span class="font-weight-normal">Tipo de Documento:</span>
          <p class="font-weight-regular mb-0">
            {{ documentType.name }}
          </p>
        </div>
        <div class="col-12 col-md-5 mb-4 mb-md-0">
          <label class="custom-file-input-label">Archivo:</label>
          <ValidationProvider
            ref="requiredDocumentProvider"
            v-slot="{ errors }"
            rules="required"
          >
            <CustomFileInput
              :value="getRequiredDocument(documentType.id).file"
              display-style="normal"
              :on-download="() => {}"
              :accept="'.pdf'"
              label-position="left"
              :error="errors[0]"
              custom-error-msg="Favor seleccionar un documento."
              :disabled="readOnly"
              @input="file => onRequiredDocumentInput(file, documentType)"
            />
          </ValidationProvider>
        </div>
      </div>
    </div>
    <div v-if="!showDocumentForm" class="container-fluid">
      <slot name="default-documents" />
    </div>
    <div
      v-if="!showDocumentForm && !hasDocuments"
      class="row justify-content-center align-items-center"
    >
      <div class="col-12">
        <ActionButton
          v-if="!readOnly"
          type="activationbutton"
          :class="{ 'pt-4': infoMessage }"
          :text="'Presione aquí para anejar documento'"
          @click="toggleShowDocumentForm"
        />
        <div
          v-if="infoMessage"
          class="bg-light-gray border-radius--light col-12 pb-2 text-muted font-size-info-message"
        >
          <i class="far fa-exclamation-circle" />
          {{ infoMessage }}
        </div>
      </div>
    </div>
    <template v-else>
      <ValidationObserver v-if="showDocumentForm" ref="documentsForm">
        <div class="container-fluid">
          <div class="row box-container">
            <div class="col-12 col-md-4 mb-4 mb-md-0">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="documentType"
                  :options="documentTypeOptions"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Documento:"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="Favor seleccionar el tipo de documento."
                />
              </ValidationProvider>
            </div>
            <div class="col-12 col-md-5 mb-4 mb-md-0">
              <CompuMinuteSelection
                v-if="isMinuteDocumentTypeSelected && isCompuMinuteSystemSelectionEnabled"
                ref="compuMinuteSelection"
                :value="minuteInfo"
                :student-sie-id="studentSieId"
                :on-go-to-draft-minute="onGoToDraftMinute"
                question="¿Desea seleccionar una minuta redactada con el Editor Minuta del sistema MiPE?"
                dropdown-label="Seleccione Minuta:"
                dropdown-placeholder="Seleccione"
                @input="$emit('minute-info-input', $event)"
              />
              <template
                v-if="
                  !isMinuteDocumentTypeSelected ||
                    !isCompuMinuteSystemSelectionEnabled ||
                    minuteInfo.useSystemMinute === false
                "
              >
                <label class="custom-file-input-label">Archivo:</label>
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <CustomFileInput
                    ref="fileUpload"
                    v-model="documentFile"
                    display-style="normal"
                    :on-download="() => {}"
                    :accept="'.pdf'"
                    label-position="left"
                    :error="errors[0]"
                    custom-error-msg="Favor seleccionar un documento."
                  />
                </ValidationProvider>
              </template>
            </div>
            <div
              class="align-items-end d-flex justify-content-end col-12 col-md-2 offset-md-1 pl-3 pr-2"
              :class="isFormValid ? 'align-items-end' : 'align-items-center'"
            >
              <ActionButton
                class="max-width-sm"
                variant="outline-danger m-1"
                i="fas fa-times-circle"
                text="Cancelar"
                :responsive="true"
                @click="toggleShowDocumentForm()"
              />
              <ActionButton
                class="max-width-sm"
                variant="success m-1"
                i="fas fa-file-plus"
                text="Añadir"
                :responsive="true"
                @click="onAddDocument()"
              />
            </div>
          </div>
        </div>
      </ValidationObserver>
      <div v-if="!showDocumentForm && hasDocuments" class="container-fluid">
        <div
          v-for="item in optionalDocuments"
          :key="item.documentId"
          class="row box-container mb-3"
        >
          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Tipo de Documento:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.documentTypeName }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Archivo:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.filename }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-12 d-block d-md-none py-2" />

          <div class="col-12 col-md-2 pt-2">
            <div
              class="h-100 d-md-flex d-lg-flex justify-content-end align-items-center"
            >
              <drop-down :items="getDocumentActions(item)" class="d-sm-block" />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 d-flex justify-content-end align-items-end pr-0">
            <ActionButton
              v-if="!readOnly && documentTypeOptions.length"
              class="max-width-sm"
              variant="success"
              i="fas fa-file-plus"
              @click="toggleShowDocumentForm()"
            >
              Añadir
            </ActionButton>
          </div>
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import DropDown from 'dropdown';
import CompuMinuteSelection from '@/components/shared/CompuMinuteSelection.vue';
import documentsService from '@/services/documents';

export default {
  name: 'DocumentsForm',
  components: {
    SectionTitle,
    ActionButton,
    CustomDropDown,
    CustomFileInput,
    DropDown,
    CompuMinuteSelection,
  },
  props: {
    documents: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    allowedDocumentTypes: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    multipleUploadsDocumentTypeIds: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    requiredDocumentTypeIds: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    hasExistingDocuments: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    infoMessage: {
      type: String,
      default: null,
    },
    minuteDocumentTypeId: {
      type: Number,
      default: null,
    },
    isCompuMinuteSystemSelectionEnabled: {
      type: Boolean,
      default: false,
    },
    studentSieId: {
      type: [Number, String],
      default: null,
    },
    minuteInfo: {
      type: Object,
      default: () => ({
        useSystemMinute: null,
        compuMinuteId: null,
        fileId: null,
        fileName: null,
      }),
    },
    onGoToDraftMinute: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      showDocumentForm: false,
      documentType: null,
      documentFile: null,
      isFormValid: true,
    };
  },
  computed: {
    isMinuteDocumentTypeSelected() {
      return (
        this.minuteDocumentTypeId != null &&
        this.documentType?.id === this.minuteDocumentTypeId
      );
    },
    requiredDocumentTypes() {
      return this.requiredDocumentTypeIds
        .map((id) => this.allowedDocumentTypes.find((type) => type.id === id))
        .filter(Boolean);
    },
    optionalDocuments() {
      return this.documents.filter(
        (document) =>
          !this.requiredDocumentTypeIds.includes(document.documentTypeId)
      );
    },
    hasDocuments() {
      return this.hasExistingDocuments || this.optionalDocuments.length > 0;
    },
    documentTypeOptions() {
      const result = this.allowedDocumentTypes.filter(
        (option) =>
          !this.requiredDocumentTypeIds.includes(option.id) &&
          (this.multipleUploadsDocumentTypeIds.includes(option.id) ||
            !this.optionalDocuments.some((e) => e.documentTypeId === option.id))
      );
      return result;
    },
    getDocumentActions() {
      const self = this;
      return (document) => [
        {
          label: 'Descargar',
          icon: 'fas fa-file-download text-green pr-1',
          click: () => this.downloadFile(document),
        },
        {
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-red pr-1',
          visible: !self.readOnly,
          click: () => {
            const index = self.documents.findIndex((doc) => doc === document);
            self.documents.splice(index, 1);

            if (document.isSystemMinute) {
              self.$emit('minute-info-input', {
                useSystemMinute: null,
                compuMinuteId: null,
                fileId: null,
                fileName: null,
              });
            }
          },
        },
      ];
    },
  },
  methods: {
    getRequiredDocument(documentTypeId) {
      return (
        this.documents.find(
          (document) => document.documentTypeId === documentTypeId
        ) || {}
      );
    },
    onRequiredDocumentInput(file, documentType) {
      const document = this.getRequiredDocument(documentType.id);

      if (document.documentTypeId) {
        document.file = file;
        document.filename = file ? file.name : null;
        return;
      }

      this.documents.push({
        documentTypeId: documentType.id,
        documentTypeName: documentType.name,
        filename: file ? file.name : null,
        file,
      });
    },
    async validate() {
      const providers = this.$refs.requiredDocumentProvider
        ? [].concat(this.$refs.requiredDocumentProvider)
        : [];
      const requiredDocumentsAreValid = (
        await Promise.all(providers.map((provider) => provider.validate()))
      ).every(Boolean);
      const optionalDocumentIsValid = this.showDocumentForm
        ? await this.$refs.documentsForm.validate()
        : true;

      return requiredDocumentsAreValid && optionalDocumentIsValid;
    },
    toggleShowDocumentForm() {
      if (this.readOnly) return;

      this.isFormValid = true;
      this.showDocumentForm = !this.showDocumentForm;
    },
    async onAddDocument() {
      const isFormValid = await this.$refs.documentsForm.validate();
      const isMinuteMode =
        this.isMinuteDocumentTypeSelected &&
        this.isCompuMinuteSystemSelectionEnabled;
      const isMinuteValid = isMinuteMode
        ? (await this.$refs.compuMinuteSelection?.validate()) ?? true
        : true;

      if (!isFormValid || !isMinuteValid) {
        this.isFormValid = isFormValid && isMinuteValid;
        return;
      }

      if (isMinuteMode && this.minuteInfo.useSystemMinute === true) {
        this.documents.push({
          documentTypeId: this.documentType.id,
          documentTypeName: this.documentType.name,
          filename: this.minuteInfo.fileName,
          documentId: this.minuteInfo.fileId,
          file: null,
          isSystemMinute: true,
        });
        this.documentType = null;
        this.$refs.documentsForm.reset();
        this.toggleShowDocumentForm();
        return;
      }

      const document = this.documentType;
      const file = {
        documentTypeId: document.id,
        documentTypeName: document.name,
        filename: this.documentFile.name,
        file: this.documentFile,
      };
      this.documents.push(file);
      this.$refs.fileUpload.removeFile();
      this.documentType = null;
      this.documentFile = null;
      this.$refs.documentsForm.reset();
      this.toggleShowDocumentForm();

      if (isMinuteMode) {
        this.$emit('minute-info-input', { useSystemMinute: false });
      }
    },
    downloadFile(document) {
      if (document.itemAttachmentId) {
        documentsService.downloadFromServer(document.itemAttachmentId);
      } else if (document.documentId && !document.file) {
        documentsService.downloadDocumentByAttachmentIdOrDocumentId(
          document.documentId
        );
      } else {
        let file = {
          filename: document.filename,
          content: document.file,
          contentType: 'application/pdf',
        };
        documentsService.downloadFile(file);
      }
    },
  },
};
</script>

<style scoped>
.box-container {
  background-color: #fafafa;
  padding: 15px 0 15px 0;
  border-radius: 5px;
}
.custom-file-input-label {
  margin-bottom: 8px;
  color: #727272;
}
.font-size-info-message {
  font-size: 0.875rem;
}
@media (max-width: 765px) {
  .max-width-sm {
    width: 100%;
  }
}
</style>
